import React from 'react'
import * as R from 'remeda'

import {
  ItemMultiSelector,
  type ItemMultiSelectorProps,
} from '~/components/elements/ItemMultiSelector'
import { type AnswerOption as AnswerOptionBase } from '~/gql/graphql'

type AnswerOption = App.PickGraphQL<AnswerOptionBase, 'option' | 'text'>

export type AnswerOptionMultiSelectorProps<TAnswerOption extends AnswerOption> =
  Omit<
    ItemMultiSelectorProps,
    'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
  > & {
    answerOptions: TAnswerOption[]
    onChange?: (options: TAnswerOption['option'][]) => void
    value: TAnswerOption['option'][]
  }

export const AnswerOptionMultiSelector = React.forwardRef(
  <TAnswerOption extends AnswerOption>(
    {
      answerOptions,
      onChange,
      value: valueProp,
      ...rest
    }: AnswerOptionMultiSelectorProps<TAnswerOption>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    type DataItem = TAnswerOption & { textField: string }
    const data = React.useMemo<DataItem[]>(
      () =>
        answerOptions.map((answerOption) => ({
          ...answerOption,
          textField: `${answerOption.option} - ${answerOption.text}`,
        })),
      [answerOptions],
    )
    const value = React.useMemo<DataItem[]>(
      () => data.filter(({ option }) => valueProp.includes(option)),
      [data, valueProp],
    )

    const handleChange = React.useCallback<
      EH<ItemMultiSelectorProps, 'onChange'>
    >(
      (value: DataItem[]) => {
        onChange?.(value.map(R.prop('option')))
      },
      [onChange],
    )

    return (
      <ItemMultiSelector
        ref={ref}
        data={data}
        dataItemKey="option"
        textField="textField"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
